/**
 * Adapted from PlayCanvas Engine's gsplat-placement.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type * as THREE from 'three'
import type {
  GSplatGraphicsAdapter,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import type { AssetId, Disposable } from '@/shared/types/core'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  GSPLAT_WORKBUFFER_UPDATE_ONCE,
  type GSplatWorkBufferUpdateMode,
  type ScalarFilterType,
} from '@/shared/types/gsplat'
import { GSPLAT_ALLOC_IDS } from './GSplatAllocId'

export interface GSplatInterval {
  readonly start: number
  readonly end: number
}

export interface GSplatParameter {
  readonly scopeId: unknown
  readonly data: unknown
}

export interface GSplatPlacementInstanceStreamsStage {
  commit(): void
  rollback(): void
  finalize(): void
}

/** Unified WorkBuffer 按资产实例应用的 WebGL 标量过滤状态。 */
export interface GSplatPlacementScalarFilter {
  readonly type: ScalarFilterType
  readonly min: number
  readonly max: number
}

/** Placement 只借用这些 format-specific Resource 字段。 */
export interface GSplatPlacementResource {
  /** 保留 Handle lease 背后的 canonical Resource，用于识别 Octree dataset 等 owner 类型。 */
  readonly resourceIdentity?: object
  /** GPU-backed Resource 的 device identity；纯 metadata Resource 可省略。 */
  readonly graphicsAdapter?: GSplatGraphicsAdapter
  readonly aabb?: THREE.Box3 | null
  readonly format?: GSplatFormat | null
  readonly streams?: Pick<GSplatStreams, 'textureDimensions'> | null
  readonly sourceGpuBytes?: number
  /** Octree World/异步 borrower 可取得独立 owner lease，避免 Handle 先销毁 metadata。 */
  acquire?(): Disposable
}

/** PlayCanvas Unified GSplatPlacement 的 backend-neutral 对齐实现。 */
export class GSplatPlacement {
  resource: GSplatPlacementResource | null
  readonly node: THREE.Object3D
  readonly intervals = new Map<number, GSplatInterval>()
  readonly id: number
  readonly allocId = GSPLAT_ALLOC_IDS.get()
  readonly lodIndex: number
  readonly parameters: Map<string, GSplatParameter> | null
  readonly parentPlacement: GSplatPlacement | null
  readonly assetId: AssetId | undefined
  lodDirty = false
  dirtyVersion = 0
  /** LOD Environment 的资产实例级期望可见状态。 */
  environmentVisible = true

  /**
   * 环境（skybox / environment）资源实例标记。
   *
   * 与 WebGL 侧 `lod/GSplatPlacement.ts` 同名语义：环境 splat 不参与主体标量直方图，
   * 且不应被 scalar filter 隐藏（豁免判定用）。主体 placement 恒为 false。
   */
  isEnvironment = false

  private readonly adapter: GSplatGraphicsAdapter
  private lodBaseDistanceValue = 5
  private lodMultiplierValue = 3
  private lodRangeMinValue = 0
  private lodRangeMaxValue = 99
  private customAabb: THREE.Box3 | null = null
  private ownedStreams: GSplatStreams | null = null
  private workBufferUpdateValue: GSplatWorkBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
  private workBufferModifierValue: CompiledGSplatWorkBufferModifier | null = null
  private scalarFilterValue: GSplatPlacementScalarFilter | null = null
  private destroyed = false

  constructor(
    adapter: GSplatGraphicsAdapter,
    resource: GSplatPlacementResource | null,
    node: THREE.Object3D,
    lodIndex = 0,
    parameters: Map<string, GSplatParameter> | null = null,
    parentPlacement: GSplatPlacement | null = null,
    id: number | null = null,
    assetId?: AssetId
  ) {
    const resourceAdapter = resource?.graphicsAdapter ?? resource?.format?.graphicsAdapter
    if (resourceAdapter && resourceAdapter !== adapter) {
      throw new Error('GSplat Placement Resource 与 adapter 不属于同一 graphics device')
    }
    if (
      resource?.graphicsAdapter &&
      resource.format &&
      resource.graphicsAdapter !== resource.format.graphicsAdapter
    ) {
      throw new Error('GSplat Placement Resource 的 adapter 与 format 不一致')
    }
    this.adapter = adapter
    this.id = id ?? parentPlacement?.id ?? 0
    this.resource = resource
    this.node = node
    this.lodIndex = lodIndex
    this.parameters = parameters ?? parentPlacement?.parameters ?? null
    this.parentPlacement = parentPlacement
    this.assetId = assetId ?? parentPlacement?.assetId
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.ownedStreams?.destroy()
    this.ownedStreams = null
    this.intervals.clear()
    this.resource = null
  }

  get isDestroyed(): boolean {
    return this.destroyed
  }

  set lodBaseDistance(value: number) {
    this.assertActive()
    if (this.lodBaseDistanceValue !== value) {
      this.lodBaseDistanceValue = value
      this.lodDirty = true
    }
  }

  get lodBaseDistance(): number {
    return this.lodBaseDistanceValue
  }

  set lodMultiplier(value: number) {
    this.assertActive()
    if (this.lodMultiplierValue !== value) {
      this.lodMultiplierValue = value
      this.lodDirty = true
    }
  }

  get lodMultiplier(): number {
    return this.lodMultiplierValue
  }

  set lodRangeMin(value: number) {
    this.assertActive()
    if (this.lodRangeMinValue !== value) {
      this.lodRangeMinValue = value
      this.lodDirty = true
    }
  }

  get lodRangeMin(): number {
    return this.lodRangeMinValue
  }

  set lodRangeMax(value: number) {
    this.assertActive()
    if (this.lodRangeMaxValue !== value) {
      this.lodRangeMaxValue = value
      this.lodDirty = true
    }
  }

  get lodRangeMax(): number {
    return this.lodRangeMaxValue
  }

  set workBufferModifier(value: CompiledGSplatWorkBufferModifier | null) {
    this.assertActive()
    this.workBufferModifierValue = value
    this.dirtyVersion++
  }

  get workBufferModifier(): CompiledGSplatWorkBufferModifier | null {
    return this.parentPlacement?.workBufferModifier ?? this.workBufferModifierValue
  }

  set scalarFilter(value: GSplatPlacementScalarFilter | null) {
    this.assertActive()
    this.scalarFilterValue = value ? Object.freeze({ ...value }) : null
    this.dirtyVersion++
  }

  get scalarFilter(): GSplatPlacementScalarFilter | null {
    return this.parentPlacement?.scalarFilter ?? this.scalarFilterValue
  }

  set workBufferUpdate(value: GSplatWorkBufferUpdateMode) {
    this.assertActive()
    if (value === GSPLAT_WORKBUFFER_UPDATE_ONCE) {
      this.workBufferUpdateValue = GSPLAT_WORKBUFFER_UPDATE_AUTO
      this.dirtyVersion++
    } else {
      this.workBufferUpdateValue = value
    }
  }

  get workBufferUpdate(): GSplatWorkBufferUpdateMode {
    // LOD file placements dynamically inherit the parent update policy, matching the
    // modifier inheritance boundary. A parent `once` is intentionally transient and
    // therefore resolves to `auto` after the parent consumes it.
    return this.parentPlacement?.workBufferUpdate ?? this.workBufferUpdateValue
  }

  markDirty(): void {
    this.assertActive()
    this.dirtyVersion++
  }

  set aabb(aabb: THREE.Box3 | null) {
    this.assertActive()
    this.customAabb = aabb?.clone() ?? null
  }

  get aabb(): THREE.Box3 {
    const aabb = this.customAabb ?? this.resource?.aabb
    if (!aabb) {
      throw new Error('GSplatPlacement.aabb 为空：必须设置 custom AABB 或 Resource AABB')
    }
    return aabb
  }

  getLodDistance(level: number): number {
    return this.lodBaseDistance * this.lodMultiplier ** level
  }

  /** Octree/Flipbook 等上游编排器复用同一 adapter/node/parameters 的唯一 child factory。 */
  createChildPlacement(
    resource: GSplatPlacementResource | null,
    lodIndex: number
  ): GSplatPlacement {
    this.assertActive()
    return new GSplatPlacement(
      this.adapter,
      resource,
      this.node,
      lodIndex,
      this.parameters,
      this,
      this.id,
      this.assetId
    )
  }

  getInstanceTexture(name: string): GSplatTextureLeaf | undefined {
    this.assertActive()
    this.ensureOwnInstanceStreams()
    return this.ownedStreams?.getTexture(name)
  }

  /** 有 parent 时动态借用 parent；这与 getInstanceTexture 操作 own container 的上游语义不同。 */
  get streams(): GSplatStreams | null {
    return this.parentPlacement?.streams ?? this.ownedStreams
  }

  ensureInstanceStreams(): void {
    this.assertActive()
    const stage = this.stageInstanceStreams()
    if (!stage) return
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
  }

  /** WorldState transaction 用：完整创建 replacement，commit 前不改变 live Placement。 */
  stageInstanceStreams(): GSplatPlacementInstanceStreamsStage | null {
    this.assertActive()
    const format = this.resource?.format
    if (!format) return null
    if (!this.ownedStreams && format.instanceStreams.length === 0) return null
    if (this.ownedStreams?.isSynchronizedWithFormat(format)) return null
    const dimensions = this.resource?.streams?.textureDimensions
    if (!dimensions) {
      throw new Error('GSplatPlacement: instance streams 要求 Resource source texture dimensions')
    }

    const staged = new GSplatStreams(this.adapter, true)
    try {
      staged.setTextureDimensions(dimensions)
      staged.syncWithFormat(format)
    } catch (error) {
      staged.destroy()
      throw error
    }
    const previous = this.ownedStreams
    let committed = false
    let settled = false
    return {
      commit: () => {
        if (settled) throw new Error('GSplat Placement instance streams stage 已结束')
        if (committed) return
        this.ownedStreams = staged
        committed = true
      },
      rollback: () => {
        if (settled) return
        if (committed) this.ownedStreams = previous
        staged.destroy()
        committed = false
        settled = true
      },
      finalize: () => {
        if (settled) return
        if (committed) previous?.destroy()
        else staged.destroy()
        settled = true
      },
    }
  }

  private ensureOwnInstanceStreams(): void {
    this.ensureInstanceStreams()
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatPlacement 已销毁')
  }
}
